/* QuadrantContentRenderer.js */

"use strict";
const _ = require("lodash");
const TWEEN = require("@tweenjs/tween.js");
const PartHeaderComponent = require("./part_header/PartHeaderComponent");

class QuadrantContentRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("parts-container");
        this._rootElem.component = this._model;
        this._swapDuration = 400;
        this._fullScreenAnimationDuration = 400;

        this._registerListeners();
        this._state = {
            opened: false,
            position: {},
        };
        this.render();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */

    /********************************************/
    _animate(time) {
        if (this._model.swapping) {
            TWEEN.update(time);
            requestAnimationFrame(this._animate);
        }
    }

    _shutdown() {
        let prom = new Promise((resolve) => {
            this._unregisterListeners();
            this._closeAllParts();
            this._model = null;
            this._rootElem.component = null;
            this._rootElem.remove();
            this._rootElem = null;
            this._state = null;
            resolve();
        });
        return prom;
    }

    _registerListeners() {
        this._closeQuadrantHandler = this._closeQuadrantHandler.bind(this);
        this._headerDoubleClickHandler = this._headerDoubleClickHandler.bind(this);
        this._animate = this._animate.bind(this);
        this._model.on("Shutdown", (this._onShutdown = this._onShutdown.bind(this)));
    }

    _unregisterListeners() {
        this._model.removeListener("Shutdown", () => this._onShutdown());
    }

    /**
     * Event handler for the "dblclick" event that is fired when a user double clicks the header area of each
     * Quadrant.
     * @param {Event} event - The event object
     */
    _headerDoubleClickHandler() {
        this._model.partHeaderDoubleClicked();
    }

    /**
     * Event handler for the "click" event that is fired when a user clicks the X (close button).
     * @param {Event} event - The event object
     */
    _closeQuadrantHandler() {
        this._model.closePartClicked();
    }

    /**
     * Creates a new header with icon and close button, and a part content container, then appends the part
     * rootElemenent into the content container
     * @param {string} partObjId - The Part to be created
     */
    _createPartDom(partInstance) {
        let partObjId = this._model.renderedPartId;

        let partContainer = document.createElement("part");
        partContainer.setAttribute("data-id", partObjId);

        let partContent = document.createElement("content");
        let partStuffFragment = document.createDocumentFragment();
        let partHeader = this._createPartHeader(this._model.getOpenedPartInfo());

        partStuffFragment.appendChild(partHeader);
        partStuffFragment.appendChild(partContent);

        partContainer.appendChild(partStuffFragment);

        partContent.appendChild(partInstance.renderer.rootElement);

        this.rootElement.appendChild(partContainer);
        return Promise.resolve(partInstance.renderer.render()).then(() => {
            return partContainer;
        });
    }

    _createPartHeader(partObj) {
        let partHeaderComponent = new PartHeaderComponent({
            title: partObj.displayName,
            iconRepoName: partObj.iconRepoName,
            quadrantName: this._model._state._name,
        });
        partHeaderComponent.on("dblclick", this._headerDoubleClickHandler);
        partHeaderComponent.on("closeClicked", this._closeQuadrantHandler);
        return partHeaderComponent.renderer.rootElement;
    }

    /**
     * Removes the Part DOM element
     * removes the event bindings associated with the Part.
     * @param {String} partId - The ID of the part to be removed
     */
    _removePartDom(partDOMNode) {
        let partHeader = partDOMNode.querySelector("header");
        if (partHeader && partHeader.component) {
            partHeader.component.shutdown();
        }
        this.rootElement.removeChild(partDOMNode);
    }

    _closeAllParts() {
        while (this.rootElement.firstChild) {
            this._removePartDom(this.rootElement.firstChild);
        }
    }

    _setTransformValues() {
        let quadName = this._model._location;
        this._transform = {};
        if (quadName === "right") {
            _.set(this._transform, "closed.left", 100);
            _.set(this._transform, "closed.right", -100);
            _.set(this._transform, "closing.left", 0);
            _.set(this._transform, "closing.right", 0);
            _.set(this._transform, "opened.left", -100);
            _.set(this._transform, "opened.right", -200);
            _.set(this._transform, "opening.left", 0);
            _.set(this._transform, "opening.right", 0);
        }
        if (quadName === "left") {
            _.set(this._transform, "closed.left", -100);
            _.set(this._transform, "closed.right", 100);
            _.set(this._transform, "closing.left", 0);
            _.set(this._transform, "closing.right", 0);
            _.set(this._transform, "opened.left", 100);
            _.set(this._transform, "opened.right", 200);
            _.set(this._transform, "opening.left", 0);
            _.set(this._transform, "opening.right", 0);
        }
        if (quadName === "bottom") {
            _.set(this._transform, "closed.top", 100);
            _.set(this._transform, "closed.bottom", -100);
            _.set(this._transform, "closing.top", 0);
            _.set(this._transform, "closing.bottom", 0);
            _.set(this._transform, "opened.top", -100);
            _.set(this._transform, "opened.bottom", -200);
            _.set(this._transform, "opening.top", 0);
            _.set(this._transform, "opening.bottom", 0);
        }
    }

    _swapParts() {
        this._model.swapping = true;
        this._setTransformValues();
        let part = this.rootElement.querySelector("part");
        return Promise.resolve()
            .then(() => {
                part.classList.add("opened");
                this.rootElement.classList.add("transitioning");
                return this._createPartDom(this._model.openedPart);
            })
            .then((newPartDom) => {
                let close = new Promise((resolve) => {
                    new TWEEN.Tween(this._transform.closing)
                        .to(this._transform.closed, this._swapDuration)
                        .easing(TWEEN.Easing.Quintic.InOut)
                        .onUpdate(() => {
                            this._applyAnimationProperties(part, this._transform.closing);
                        })
                        .onComplete(() => {
                            resolve();
                        })
                        .start();
                });
                let open = new Promise((resolve) => {
                    new TWEEN.Tween(this._transform.opening)
                        .to(this._transform.opened, this._swapDuration)
                        .easing(TWEEN.Easing.Quintic.InOut)
                        .onUpdate(() => {
                            this._applyAnimationProperties(newPartDom, this._transform.opening);
                        })
                        .onComplete(() => {
                            resolve();
                        })
                        .start();
                });
                requestAnimationFrame(this._animate);
                return Promise.all([open, close]);
            })
            .then(() => {
                this._removePartDom(part);
                let newPart = this.rootElement.querySelector("part");
                newPart.setAttribute("style", "");
                this.rootElement.classList.remove("transitioning");
                this._model.swapping = false;
            })
            .catch((err) => {
                this._model.swapping = false;
                throw err;
            });
    }

    _applyAnimationProperties(domEl, props) {
        for (let prop in props) {
            if (prop === "top") {
                domEl.style.transform = "translate(0%, " + props[prop] + "%)";
            }
            if (prop === "left") {
                domEl.style.transform = "translate(" + props[prop] + "%, 0%)";
            }
        }
    }

    _setFullScreen() {
        return new Promise((resolve, reject) => {
            if (!this.rootElement.classList.contains("full-screen")) {
                try {
                    let boundingClientRect = this.rootElement.getBoundingClientRect();
                    let parentboundingRect = this.rootElement.parentNode.getBoundingClientRect();
                    this._state.position.top = _.clone(boundingClientRect.top);
                    this._state.position.right = _.clone(
                        window.innerWidth - boundingClientRect.right
                    );
                    this._state.position.bottom = _.clone(
                        window.innerHeight - parentboundingRect.bottom
                    );
                    this._state.position.left = _.clone(boundingClientRect.left);
                    //console.log(this.rootElement.parentNode.getBoundingClientRect());
                    // Create a base screen position to animate from
                    this.rootElement.style.top = this._state.position.top + "px";
                    this.rootElement.style.right = this._state.position.right + "px";
                    this.rootElement.style.bottom = this._state.position.bottom + "px";
                    this.rootElement.style.left = this._state.position.left + "px";
                    //this._setAttrsForFullScreen();
                    this.rootElement.setAttribute("style", this._sizePropertyName);
                    this.rootElement.style.position = "fixed";

                    let tweenProps = _.clone(this._state.position);
                    let newSize = {top: 0, right: 0, bottom: 0, left: 0};
                    this.rootElement.classList.add("transitioning-full-screen");
                    this._model.swapping = true;

                    new TWEEN.Tween(tweenProps)
                        .to(newSize, this._fullScreenAnimationDuration)
                        .easing(TWEEN.Easing.Quintic.InOut)
                        .onUpdate(() => {
                            this.rootElement.style.top = tweenProps.top + "px";
                            this.rootElement.style.right = tweenProps.right + "px";
                            this.rootElement.style.bottom = tweenProps.bottom + "px";
                            this.rootElement.style.left = tweenProps.left + "px";
                        })
                        .onComplete(() => {
                            this._model.swapping = false;
                            this.rootElement.classList.add("full-screen");
                            this.rootElement.classList.remove("transitioning-full-screen");
                            //this._cleanupLocalStyles();
                            resolve();
                        })
                        .start();

                    requestAnimationFrame(this._animate);
                } catch (e) {
                    reject(e);
                }
            } else {
                resolve();
            }
        });
    }

    _unsetFullScreen() {
        return Promise.resolve().then(() => {
            return new Promise((resolve, reject) => {
                if (this.rootElement.classList.contains("full-screen")) {
                    try {
                        let parentboundingRect =
                            this.rootElement.parentNode.getBoundingClientRect();
                        let newSize = _.clone(this._state.position);
                        newSize.bottom = window.innerHeight - parentboundingRect.bottom;
                        let quadName = this._model._location;
                        if (!this._model.getOpenedPartId()) {
                            if (quadName === "left") {
                                newSize.right = window.innerWidth - newSize.left;
                            }
                            if (quadName === "right") {
                                newSize.left = window.innerWidth - newSize.right;
                            }
                            if (quadName === "bottom") {
                                newSize.top = window.innerHeight - newSize.bottom;
                            }
                        }
                        let tweenProps = {top: 0, right: 0, bottom: 0, left: 0};
                        this.rootElement.classList.add("transitioning-full-screen");
                        this.rootElement.style.top = tweenProps.top + "px";
                        this.rootElement.style.right = tweenProps.right + "px";
                        this.rootElement.style.bottom = tweenProps.bottom + "px";
                        this.rootElement.style.left = tweenProps.left + "px";
                        //this._setAttrsForFullScreen();
                        this.rootElement.style.position = "fixed";
                        this.rootElement.classList.remove("full-screen");
                        this._model.swapping = true;

                        new TWEEN.Tween(tweenProps)
                            .to(newSize, this._fullScreenAnimationDuration)
                            .easing(TWEEN.Easing.Quintic.InOut)
                            .onUpdate(() => {
                                //console.log(tweenProps)
                                this.rootElement.style.top = tweenProps.top + "px";
                                this.rootElement.style.right = tweenProps.right + "px";
                                this.rootElement.style.bottom = tweenProps.bottom + "px";
                                this.rootElement.style.left = tweenProps.left + "px";
                            })
                            .onComplete(() => {
                                this._model.swapping = false;
                                this.rootElement.classList.remove("transitioning-full-screen");
                                //this._elementSize = this._size;
                                //this._model.setFullScreen(false, true);
                                this._cleanupLocalStyles();
                                resolve();
                            })
                            .start();
                        requestAnimationFrame(this._animate);
                    } catch (e) {
                        reject(e);
                    }
                } else {
                    resolve();
                }
            });
        });
    }

    _cleanupLocalStyles() {
        let styleString = this.rootElement.getAttribute("style");
        if (styleString !== null) {
            let inlineStylesArray = this.rootElement.getAttribute("style").split(";");
            let height, minHeight, width, minWidth;
            for (let index in inlineStylesArray) {
                if (inlineStylesArray[index].indexOf(":") !== -1) {
                    if (
                        inlineStylesArray[index]
                            .substring(0, inlineStylesArray[index].indexOf(":"))
                            .trim() === "width"
                    ) {
                        width = inlineStylesArray[index]
                            .substring(
                                inlineStylesArray[index].indexOf(":") + 1,
                                inlineStylesArray[index].length
                            )
                            .trim();
                    }
                    if (
                        inlineStylesArray[index]
                            .substring(0, inlineStylesArray[index].indexOf(":"))
                            .trim() === "min-width"
                    ) {
                        minWidth = inlineStylesArray[index]
                            .substring(
                                inlineStylesArray[index].indexOf(":") + 1,
                                inlineStylesArray[index].length
                            )
                            .trim();
                    }
                    if (
                        inlineStylesArray[index]
                            .substring(0, inlineStylesArray[index].indexOf(":"))
                            .trim() === "height"
                    ) {
                        height = inlineStylesArray[index]
                            .substring(
                                inlineStylesArray[index].indexOf(":") + 1,
                                inlineStylesArray[index].length
                            )
                            .trim();
                    }
                    if (
                        inlineStylesArray[index]
                            .substring(0, inlineStylesArray[index].indexOf(":"))
                            .trim() === "min-height"
                    ) {
                        minHeight = inlineStylesArray[index]
                            .substring(
                                inlineStylesArray[index].indexOf(":") + 1,
                                inlineStylesArray[index].length
                            )
                            .trim();
                    }
                }
            }
            this.rootElement.removeAttribute("style");
            this.rootElement.setAttribute("style", "");
            if (width) {
                this.rootElement.style.width = width;
            }
            if (minWidth) {
                this.rootElement.style.minWidth = minWidth;
            }
            if (height) {
                this.rootElement.style.height = height;
            }
            if (minHeight) {
                this.rootElement.style.minHeight = minHeight;
            }
        }
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */

    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        return Promise.resolve()
            .then(() => {
                return new Promise((resolve) => {
                    let part = this.rootElement.querySelector("part");
                    if (part) {
                        if (this._model.openedPart) {
                            let partId = part.getAttribute("data-id");
                            if (partId !== this._model.renderedPartId) {
                                resolve(this._swapParts());
                            } else {
                                // console.log("the part is already open");
                                resolve();
                            }
                        } else {
                            // console.log("unsetfullscreen")
                            this._unsetFullScreen().then(() => {
                                this._closeAllParts();
                                resolve();
                            });
                        }
                    } else {
                        if (this._model.openedPart) {
                            this._createPartDom(this._model.openedPart).then(() => resolve());
                        } else {
                            // console.log("nothing is set for this");
                            resolve();
                        }
                    }
                });
            })
            .then(() => {
                if (this._model.fullScreen) {
                    return this._setFullScreen();
                } else {
                    return this._unsetFullScreen();
                }
            });
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */

    /*******************************************/
    _onShutdown() {
        this._shutdown();
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/
    //TODO - Nobody should be using methods on a Renderer directly so make all these methods private
    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = QuadrantContentRenderer;
